import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import {
  FEEDBACK_PRIORITIES, FEEDBACK_STATUSES, FEEDBACK_STATUS_TRANSITIONS,
  feedbackCategoryLabel, feedbackPriorityLabel, feedbackSubmitToLabel,
} from '../../data/feedbackConstants';
import { canManageSchoolFeedback } from '../../utils/feedbackPermissions';
import FeedbackStatusBadge from './FeedbackStatusBadge';
import FeedbackAttachments from './FeedbackAttachments';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { getApiErrorMessage } from '../../utils/feedbackDownload';

function employeeLabel(emp) {
  const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
  return name ? `${name} (${emp.employee_code})` : emp.employee_code;
}

export default function FeedbackDetail({ scope = 'platform' }) {
  const isSchoolScope = scope === 'school';
  const isCrmScope = scope === 'crm';
  const listBasePath = isSchoolScope ? '/school-feedback' : isCrmScope ? '/crm/feedback' : '/feedback';
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const isSuperAdmin = user?.role_slug === 'super_admin';
  const canManage = isCrmScope
    || (isSuperAdmin && !isSchoolScope)
    || (isSchoolScope && canManageSchoolFeedback(user));
  const canManageStatusOnly = isCrmScope;
  const canViewAttachments = canManage;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [assignedEmployeeId, setAssignedEmployeeId] = useState('unassigned');
  const [employees, setEmployees] = useState([]);
  const [internalNote, setInternalNote] = useState('');
  const [reply, setReply] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  const loadEmployees = () => {
    if (isSchoolScope || !isSuperAdmin) return;
    api.get('/crm/employees')
      .then((res) => setEmployees((res.data?.data?.items || []).filter((e) => e.status === 'active')))
      .catch(() => setEmployees([]));
  };

  const load = () => {
    setLoading(true);
    api.get(`/feedback/${id}`)
      .then((res) => {
        const d = res.data?.data;
        setData(d);
        setStatus(d?.status || '');
        setPriority(d?.priority || '');
        setAssignedEmployeeId(d?.assigned_employee_id ? String(d.assigned_employee_id) : 'unassigned');
      })
      .catch((e) => {
        toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
        setData(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { loadEmployees(); }, [isSchoolScope, isSuperAdmin]);

  const allowedStatuses = data?.status
    ? [data.status, ...(FEEDBACK_STATUS_TRANSITIONS[data.status] || [])]
    : FEEDBACK_STATUSES.map((s) => s.value);

  const internalNotes = (data?.activity || []).filter((a) => a.action === 'internal_note');

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const payload = canManageStatusOnly ? { status } : { status, priority };
      if (!isSchoolScope && isSuperAdmin) {
        payload.assigned_employee_id = assignedEmployeeId === 'unassigned'
          ? null
          : Number(assignedEmployeeId);
      }
      await api.patch(`/feedback/${id}`, payload);
      toast({ title: 'Updated', description: 'Feedback updated successfully.' });
      load();
    } catch (e) {
      toast({ title: 'Update failed', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNote = async () => {
    const note = internalNote.trim();
    if (!note) {
      toast({ title: 'Note required', description: 'Please enter an internal note.', variant: 'destructive' });
      return;
    }
    setSavingNote(true);
    try {
      await api.post(`/feedback/${id}/internal-note`, { message: note });
      setInternalNote('');
      toast({ title: 'Note saved', description: 'Internal note added successfully.' });
      load();
    } catch (e) {
      toast({ title: 'Save failed', description: await getApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setSavingNote(false);
    }
  };

  const handleReply = async () => {
    if (!reply.trim()) {
      toast({ title: 'Reply required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await api.post(`/feedback/${id}/reply`, { message: reply.trim() });
      setReply('');
      toast({ title: 'Reply sent', description: 'The user has been notified.' });
      load();
    } catch (e) {
      toast({ title: 'Reply failed', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/feedback/${id}`);
      toast({ title: 'Moved to Bin', description: 'Feedback can be restored from Bin.' });
      navigate(listBasePath);
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  const backPath = listBasePath;
  const backLabel = isCrmScope ? 'My Assigned Feedback' : isSchoolScope ? 'Feedback Management' : 'Feedback Management';

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  if (!data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild><Link to={backPath}><ArrowLeft className="h-4 w-4 mr-2" />Back</Link></Button>
        <p className="text-muted-foreground">Feedback not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="feedback-detail">
      <Button variant="ghost" size="sm" asChild>
        <Link to={backPath}><ArrowLeft className="h-4 w-4 mr-1" />{backLabel}</Link>
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <p className="text-sm font-mono text-muted-foreground">{data.feedback_code}</p>
          <h1 className="text-2xl font-bold mt-1">{data.title}</h1>
          <p className="text-muted-foreground mt-1">
            {isSchoolScope
              ? `${data.user_name} (${data.user_role_slug})`
              : `${data.school_name} · ${data.user_name} (${data.user_role_slug})`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FeedbackStatusBadge status={data.status} />
          {canManage && !canManageStatusOnly && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive">
                  <Trash2 className="h-4 w-4 mr-1" />Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete feedback?</AlertDialogTitle>
                  <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader><CardTitle>Description</CardTitle></CardHeader>
            <CardContent><p className="whitespace-pre-wrap">{data.description}</p></CardContent>
          </Card>

          <FeedbackAttachments attachments={data.attachments} canAccess={canViewAttachments} />

          <Card>
            <CardHeader><CardTitle>Replies</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {(data.replies || []).length === 0 ? (
                <p className="text-muted-foreground text-sm">No replies yet.</p>
              ) : (
                <ul className="space-y-3">
                  {data.replies.map((r) => (
                    <li key={r.id} className="rounded-lg border p-3 bg-muted/30">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">{r.admin_name}</span>
                        <span className="text-muted-foreground">{r.created_at?.slice(0, 16).replace('T', ' ')}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{r.message}</p>
                    </li>
                  ))}
                </ul>
              )}
              {canManage && (
                <div className="space-y-2 pt-2 border-t">
                  <Label>Reply to user</Label>
                  <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Write a reply..." />
                  <Button onClick={handleReply} disabled={saving}>Send Reply</Button>
                </div>
              )}
            </CardContent>
          </Card>

          {canManage && (
            <Card>
              <CardHeader><CardTitle>Internal Notes</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {internalNotes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No internal notes yet.</p>
                ) : (
                  <ul className="space-y-3 max-h-64 overflow-y-auto">
                    {internalNotes.map((note) => (
                      <li key={note.id} className="rounded-lg border border-amber-200/60 bg-amber-50/50 dark:bg-amber-950/20 p-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium">{note.actor_name || 'Admin'}</span>
                          <span className="text-muted-foreground">{note.created_at?.slice(0, 16).replace('T', ' ')}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{note.message}</p>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="space-y-2 pt-2 border-t">
                  <Label>Add internal note</Label>
                  <Textarea
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                    rows={3}
                    placeholder="Visible to admins only..."
                  />
                  <Button onClick={handleSaveNote} disabled={savingNote}>Save Note</Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle>Activity History</CardTitle></CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm max-h-64 overflow-y-auto">
                {(data.activity || []).map((a) => (
                  <li key={a.id} className={`border-b border-border/50 pb-2 ${a.is_internal ? 'opacity-80' : ''}`}>
                    <div className="flex justify-between">
                      <span className="font-medium capitalize">{a.action?.replace(/_/g, ' ')}</span>
                      {a.is_internal ? <span className="text-xs text-amber-600">Internal</span> : null}
                    </div>
                    <p className="text-muted-foreground text-xs">{a.actor_name} · {a.created_at?.slice(0, 16).replace('T', ' ')}</p>
                    {a.message && <p className="mt-1 text-xs">{a.message}</p>}
                    {a.old_value && a.new_value && (
                      <p className="text-xs text-muted-foreground">{a.old_value} → {a.new_value}</p>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          {canManage && (
            <Card>
              <CardHeader><CardTitle>Manage</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[...new Set(allowedStatuses)].map((s) => (
                        <SelectItem key={s} value={s}>
                          {FEEDBACK_STATUSES.find((x) => x.value === s)?.label || s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {!canManageStatusOnly && (
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={priority} onValueChange={setPriority}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FEEDBACK_PRIORITIES.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {!isSchoolScope && isSuperAdmin && (
                  <div className="space-y-2">
                    <Label>Assign to</Label>
                    <Select value={assignedEmployeeId} onValueChange={setAssignedEmployeeId}>
                      <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={String(emp.id)}>{employeeLabel(emp)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button className="w-full" onClick={handleUpdate} disabled={saving}>Save Changes</Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle>Submission Info</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div><span className="text-muted-foreground">Category</span><p>{feedbackCategoryLabel(data.category)}</p></div>
              <div><span className="text-muted-foreground">Submit to</span><p>{feedbackSubmitToLabel(data.submit_to)}</p></div>
              <div><span className="text-muted-foreground">Priority</span><p>{feedbackPriorityLabel(data.priority)}</p></div>
              {!isSchoolScope && !isCrmScope && (
                <div><span className="text-muted-foreground">Assigned to</span><p>{data.assigned_to_name || 'Unassigned'}</p></div>
              )}
              <div><span className="text-muted-foreground">Browser</span><p>{data.browser || '—'}</p></div>
              <div><span className="text-muted-foreground">Device</span><p>{data.device || '—'}</p></div>
              <div><span className="text-muted-foreground">App version</span><p>{data.app_version || '—'}</p></div>
              {data.page_url && <div><span className="text-muted-foreground">Page</span><p className="break-all text-xs">{data.page_url}</p></div>}
              <div><span className="text-muted-foreground">Submitted</span><p>{data.created_at?.slice(0, 16).replace('T', ' ')}</p></div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
